import { DateSpecialValue } from "@/components/UiKit/AerosDatePicker/AerosDatePickerWithCustomActions"
import type { OrderUpdateInput } from "@/graphql/codegen/graphql"
import type { PurchaseOrderForm } from "@/screens/Procurement/PurchaseOrder/types"
import { toStringOrNull } from "@/utils/forms"
import { resolveSpecialDate } from "@/utils/purchaseOrder"

export const createOrderUpdateInput = (
  order: PurchaseOrderForm,
  additionalFields: Partial<PurchaseOrderForm> = {}
): OrderUpdateInput => {
  const mergedOrder = {
    ...order,
    ...additionalFields,
  }

  return {
    orderId: mergedOrder.orderId,
    clonedFromOrderId: mergedOrder.clonedFromOrderId,
    status: mergedOrder.status,
    type: mergedOrder.type,
    versionNumber: mergedOrder.versionNumber,
    sentDate: toStringOrNull(mergedOrder.sentDate ?? ""),
    isDeleted: mergedOrder.isDeleted,

    confirmedDeliveryDate: toStringOrNull(mergedOrder.confirmedDeliveryDate ?? ""),
    expectedDeliveryDate: toStringOrNull(mergedOrder.expectedDeliveryDate ?? ""),

    // Special props
    requestedDeliveryDate: resolveSpecialDate(mergedOrder.requestedDeliveryDate, DateSpecialValue.ASAP),
    isRequestedDeliveryAsap: mergedOrder.requestedDeliveryDate === DateSpecialValue.ASAP,
    confirmedShipmentDate: resolveSpecialDate(mergedOrder.confirmedShipmentDate, DateSpecialValue.TBD),
    isConfirmedShipmentTbd: mergedOrder.confirmedShipmentDate === DateSpecialValue.TBD,

    shippedDate: toStringOrNull(mergedOrder.shippedDate ?? ""),
    domesticPortDate: toStringOrNull(mergedOrder.domesticPortDate ?? ""),
    expectedReceivedDate: toStringOrNull(mergedOrder.expectedReceivedDate ?? ""),
    receivedDate: toStringOrNull(mergedOrder.receivedDate ?? ""),

    deliveryLocationId: toStringOrNull(mergedOrder.deliveryLocationId ?? ""),
    deliveryTerms: toStringOrNull(mergedOrder.deliveryTerms ?? ""),
    freightTerms: mergedOrder.freightTerms ?? undefined,
    containerTrackingNumber: toStringOrNull(mergedOrder.containerTrackingNumber ?? ""),
    destinationBillingLocationId: mergedOrder.destinationBillingLocationId,
    destinationLocationAssociationId: mergedOrder.destinationLocationAssociationId,
    warehouseAreaId: mergedOrder.warehouseAreaId,
    destinationOrderLocationId: mergedOrder.destinationOrderLocationId,

    note: mergedOrder.note,

    orderLines: (mergedOrder.orderLines ?? []).map(
      ({
        numberOfPallets,
        pricePerUom,
        quantityInUom,
        productId,
        uomId,
        priceUomId,
        isManualPriceSet,
        productVersionId,
      }) => ({
        numberOfPallets,
        pricePerUom,
        quantityInUom,
        productId,
        uomId,
        priceUomId,
        isManualPriceSet,
        productVersionId,
      })
    ),
    // workaround for handling empty ship status AK-3838
    shipStatus: mergedOrder.shipStatus || null,
    shipViaCompanyId: toStringOrNull(mergedOrder.shipViaCompanyId ?? ""),

    shippingLegs: mergedOrder.shippingLegs?.map((leg) => ({
      sourceLocationId: toStringOrNull(leg.sourceLocationId ?? ""),
      shipViaCompanyId: toStringOrNull(leg.shipViaCompanyId ?? ""),
      containerTrackingNumber: toStringOrNull(leg.containerTrackingNumber ?? ""),
      destinationLocationId: toStringOrNull(leg.destinationLocationId ?? ""),
      shipStatus: leg.shipStatus || null,
    })),

    sourceBillingLocationId: mergedOrder.sourceBillingLocationId,
    sourceLocationAssociationId: mergedOrder.sourceLocationAssociationId,
    sourceOrderLocationId: mergedOrder.sourceOrderLocationId,
  }
}
